import React, { useCallback, useEffect } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
} from '@mui/material'
import { Order } from '../../store/orders/orders.types'
import { Company } from '../../store/company/company.types'
import { useUpdateOrderConfirmationDialog } from './update-order-confirmation-dialog.hooks'
import {
  useUpdateOrderMutation,
  useUpdateOrderStatusMutation,
} from '../../store/orders/orders.api'
import { useUpdateCompanyByIdMutation } from '../../store/company/company.api'
import { skipToken } from '@reduxjs/toolkit/query'
import { useNavigate } from 'react-router-dom'
import useSubmitLock from 'hooks/use-submit-lock'

type UOCProps = {
  order: Order
  company?: Company
  handleClose: () => void
} & DialogProps

const UpdateOrderConfirmationDialog = ({
  open,
  order,
  company,
  handleClose,
}: UOCProps) => {
  const navigate = useNavigate()
  const { isSubmitting, wrapClick } = useSubmitLock()

  const { isError, Message } = useUpdateOrderConfirmationDialog(order)
  const [updateOrder] = useUpdateOrderMutation()
  const [updateOrderStatus] = useUpdateOrderStatusMutation()
  const [updateCompany] = useUpdateCompanyByIdMutation()

  const handleAgree = useCallback(async () => {
    try {
      // Update order first - use unwrap() to throw errors properly
      await updateOrder({ order, orderId: order?.orderId }).unwrap()
      await updateOrderStatus({
        orderId: order.orderId ?? skipToken,
        status: 'ACTIVE',
      }).unwrap()
      
      // Update company if provided
      if (company && company.id) {
        await updateCompany(company).unwrap()
      }

      handleClose()
      navigate('/order')
    } catch (error) {
      console.error('Error while updating order or company', error)
      // Don't close dialog if there's an error
    }
  }, [order, company, updateOrder, updateOrderStatus, updateCompany, handleClose, navigate])

  useEffect(() => {
    if (open && !isError) {
      handleAgree()
    }
  }, [open, isError, handleAgree])

  if (!open || !isError) {
    return null
  }

  return (
    <Dialog open={open} maxWidth="lg">
      <DialogTitle>Update Order Details</DialogTitle>

      <DialogContent>
        <DialogContentText>
          The following details are not provided. Are you sure to update the information?
          <Message />
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button
          color="error"
          variant="contained"
          onClick={handleClose}
          sx={{ textTransform: 'none', borderRadius: '5px' }}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={wrapClick(handleAgree)}
          disabled={isSubmitting}
          sx={{
            textTransform: 'none',
            borderRadius: '5px',
            '&.Mui-disabled': {
              bgcolor: 'action.disabledBackground',
              color: 'action.disabled',
            },
          }}
          autoFocus
        >
          Update Anyway
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UpdateOrderConfirmationDialog
